/**
 * Outlet Settings — Phase 4 (timezone + cutoff_hour).
 *
 * Minimal operator-facing page for the outlet-local time configuration
 * that every business-day boundary in Sales Capture now reads. Behavior
 * contract:
 *   - `timezone` changes are IMMEDIATELY reflected in every downstream
 *     query (Comparison Engine, dashboards, demand buckets, confidence
 *     lookback). See `utils/timezone.get_outlet_timezone`.
 *   - `cutoff_hour` is STORED ONLY in this sprint; query-side rollover
 *     is deferred (the page shows a small caveat to that effect).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { outletSettingsApi, outletsApi } from '../lib/api';
import { toast } from 'sonner';
import { Gear, Info, FloppyDisk, Timer } from '@phosphor-icons/react';

// Short-list of tz options we offer up-front — users can still PUT any
// valid zoneinfo key via API. This keeps the dropdown sane for operators.
const TIMEZONE_CHOICES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Bangkok',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
];

const CUTOFF_CHOICES = Array.from({ length: 24 }, (_, h) => h);

function useOutlets() {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    outletsApi.list().then((res) => {
      if (!alive) return;
      setOutlets(res.data || []);
    }).catch(() => {
      toast.error('Could not load outlets');
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { outlets, loading };
}

export default function OutletSettingsPage() {
  const { outlets, loading: outletsLoading } = useOutlets();
  const [selectedId, setSelectedId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Default to first outlet once loaded
  useEffect(() => {
    if (!selectedId && outlets.length) {
      setSelectedId(outlets[0].id || outlets[0].outlet_id);
    }
  }, [outlets, selectedId]);

  // Load settings when outlet changes
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    setLoading(true);
    outletSettingsApi.get(selectedId)
      .then((res) => { if (alive) setSettings(res.data); })
      .catch(() => { toast.error('Could not load outlet settings'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [selectedId]);

  const selectedOutlet = useMemo(
    () => outlets.find((o) => (o.id || o.outlet_id) === selectedId),
    [outlets, selectedId],
  );

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await outletSettingsApi.put(selectedId, {
        timezone: settings.timezone,
        cutoff_hour: Number(settings.cutoff_hour),
      });
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070B10] text-white" data-testid="outlet-settings-page">
      <div className="px-4 lg:px-6 py-5 max-w-[960px] mx-auto space-y-5">
        <div>
          <div className="flex items-center gap-3">
            <Gear size={22} className="text-[#D4FF00]" weight="duotone" />
            <h1 className="text-[24px] font-semibold tracking-tight text-zinc-100">
              Outlet Settings
            </h1>
          </div>
          <p className="text-[13px] text-zinc-500 mt-1">
            Time-configuration that drives every business-day boundary in Sales Capture.
          </p>
        </div>

        {/* Outlet picker */}
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
            Outlet
          </label>
          <select
            value={selectedId || ''}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={outletsLoading}
            className="w-full bg-[#0F1115] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-zinc-100 focus:border-[#D4FF00]/40 focus:outline-none"
            data-testid="os-outlet-select"
          >
            {outlets.map((o) => (
              <option key={o.id || o.outlet_id} value={o.id || o.outlet_id}>
                {o.name || o.display_name || o.id}
              </option>
            ))}
          </select>
        </div>

        {loading || !settings ? (
          <div className="rounded-xl bg-[#0F1115] border border-white/5 p-6 text-center text-[13px] text-zinc-500">
            Loading settings…
          </div>
        ) : (
          <div className="rounded-xl bg-[#0F1115] border border-white/5 p-5 space-y-5">
            {/* Timezone */}
            <div data-testid="os-timezone-row">
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                Timezone
              </label>
              <select
                value={settings.timezone}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="w-full bg-[#0A0D12] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-zinc-100 focus:border-[#D4FF00]/40 focus:outline-none"
                data-testid="os-timezone-select"
              >
                {TIMEZONE_CHOICES.concat(
                  TIMEZONE_CHOICES.includes(settings.timezone) ? [] : [settings.timezone]
                ).map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-2">
                Controls "today", comparison anchors, demand buckets, and the 30-day confidence lookback.
              </p>
            </div>

            {/* Cutoff hour */}
            <div data-testid="os-cutoff-row">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500">
                  Business-Day Cutoff Hour
                </label>
                {!settings.cutoff_hour_behavior_active && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 uppercase tracking-wider"
                    data-testid="os-cutoff-deferred-badge"
                  >
                    Stored · behavior deferred
                  </span>
                )}
              </div>
              <select
                value={settings.cutoff_hour}
                onChange={(e) => setSettings({ ...settings, cutoff_hour: Number(e.target.value) })}
                className="w-full bg-[#0A0D12] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-zinc-100 focus:border-[#D4FF00]/40 focus:outline-none"
                data-testid="os-cutoff-select"
              >
                {CUTOFF_CHOICES.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
              <div className="flex items-start gap-2 mt-3 text-[11px] text-zinc-500">
                <Info size={12} className="text-amber-500/70 shrink-0 mt-0.5" weight="duotone" />
                <p>
                  The business day runs from the cutoff hour until the same hour the next day.
                  The value is saved but <span className="text-zinc-400">not yet applied</span> to
                  query boundaries — that migration ships in a follow-up sprint.
                </p>
              </div>
            </div>

            {/* Save */}
            <div className="flex items-center justify-end pt-2 border-t border-white/5">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/30 text-[#D4FF00] text-[13px] font-medium flex items-center gap-2 hover:bg-[#D4FF00]/15 transition disabled:opacity-50"
                data-testid="os-save-btn"
              >
                <FloppyDisk size={14} weight="duotone" />
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}

        {/* Context footer */}
        <p className="text-[10px] text-zinc-600 text-center pt-2">
          Timezone changes reflect immediately across every dashboard.
          Cutoff-hour rollover migration is scheduled for a follow-up sprint.
        </p>
      </div>
    </div>
  );
}
